'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { Clock } from 'lucide-react'

interface ReadingProgressProps {
  /** Total estimated reading time in minutes */
  totalMinutes: number
}

export default function ReadingProgress({ totalMinutes }: ReadingProgressProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [minutesLeft, setMinutesLeft] = useState(totalMinutes)
  const barRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const progress = docHeight > 0 ? Math.min(scrollY / docHeight, 1) : 0

    // Update progress bar width via CSS custom property
    if (barRef.current) {
      barRef.current.style.transform = `scaleX(${progress})`
    }

    // Only show after scrolling past ~100px
    setIsVisible(scrollY > 100)

    // Calculate remaining reading time
    const remaining = Math.ceil(totalMinutes * (1 - progress))
    setMinutesLeft(remaining)

    // Mark complete at 98%+
    setIsComplete(progress >= 0.98)
  }, [totalMinutes])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // initial
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const pillVisible = isVisible && !isComplete

  return (
    <>
      {/* Progress Bar - Fixed at top of viewport */}
      <div
        ref={barRef}
        className="fixed top-0 left-0 right-0 h-[3px] z-50 pointer-events-none"
        style={{
          transformOrigin: '0%',
          transform: 'scaleX(0)',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        <div
          className="w-full h-full"
          style={{
            background: 'linear-gradient(90deg, #DC2626, #EF4444)',
            boxShadow: '0 0 12px rgba(220, 38, 38, 0.4), 0 0 4px rgba(220, 38, 38, 0.2)',
          }}
        />
      </div>

      {/* Reading Time Countdown Pill */}
      <div
        className="fixed bottom-6 right-6 z-40 pointer-events-none"
        style={{
          opacity: pillVisible ? 1 : 0,
          transform: pillVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
        }}
      >
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
          style={{
            background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.9) 0%, rgba(10, 10, 10, 0.85) 100%)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(220, 38, 38, 0.06)',
          }}
        >
          <Clock size={14} className="text-red-500" />
          <span className="text-zinc-300">
            ~{minutesLeft} min left
          </span>
        </div>
      </div>
    </>
  )
}
